import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { UsersService } from 'src/app/users/users.service';
import { MultiTariffService } from '../multi-tariff.service';
import { Category, CURRENCY, Profile, ProfileInfoResponse, Rate, SaveProfileResponse, ValidationFlags } from '../multi-tariff.model';

@Component({
	selector: 'app-multi-tariff-edit-profile',
	templateUrl: './edit-profile.component.html',
	styleUrls: ['./edit-profile.component.css']
})
export class EditProfileComponent implements OnInit {

	currencies = CURRENCY;

	profile: Profile = { id: 0 };
	rates: Rate[] = [];
	categories: Category[] = [];

	deletedRateIndex: number = null;

	invalidFields: ValidationFlags = null;
	isNewProfile = true;
	hasWriteAccess = false;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private usersService: UsersService,
		private notificationMessage: NotificationMessageService,
		private translateService: TranslateService,
		public multiTariffService: MultiTariffService
	) { }

	ngOnInit() {
		if (!this.usersService.hasAccessFunction('global_multi_tariff_profiles'))
			return this.router.navigate(['/unauthorized']);

		this.hasWriteAccess = this.usersService.hasAccessFunction('global_multi_tariff_profiles', 'write');

		return this.route.params.subscribe((params) => {
			const profileId = +params.profileId || 0;
			this.isNewProfile = !profileId;

			if (!profileId) {
				this.profile = { id: 0 };
				this.rates = [];
				this.categories = [];
				return;
			}

			this.getProfile(profileId);
		});
	}

	getProfile(profileId: number) {
		this.multiTariffService.getProfileInfo(profileId).subscribe((data: ProfileInfoResponse) => {
			if (!data || !data.profile)
				return this.router.navigateByUrl('/404');

			this.profile = data.profile;
			this.rates = data.rates || [];
			this.categories = data.categories || [];

			this.multiTariffService.mapRatesAfterLoad(this.rates);
			this.multiTariffService.mapCategoryRatesAfterLoad(this.categories, this.rates);

			return;
		});
	}

	onRatesChange(data: { rates: Rate[], deletedRateIndex?: number }) {
		this.rates = data.rates;
		this.deletedRateIndex = data.deletedRateIndex === undefined ? null : data.deletedRateIndex;
	}

	onCategoriesChange(categories: Category[]) {
		this.categories = categories;
	}

	saveProfile() {
		const validation = this.multiTariffService.saveValidation(
			cloneDeep(this.profile),
			cloneDeep(this.rates),
			cloneDeep(this.categories)
		);

		if (this.multiTariffService.hasInvalidField(validation.invalidFields)) {
			this.invalidFields = validation.invalidFields;
			this.notificationMessage.setMessage(this.translateService.instant('g.invalid_input'), { clearOnXTimeNavigate: 1 });
			return;
		}

		this.invalidFields = null;

		const { profile, rates, categories } = validation.data;

		this.multiTariffService.save(profile, rates, categories).subscribe((res: SaveProfileResponse) => {
			if (res && res.invalidFields) {
				this.invalidFields = res.invalidFields;
				this.notificationMessage.setMessage(this.translateService.instant('g.invalid_input'), { clearOnXTimeNavigate: 1 });
				return;
			}

			this.notificationMessage.setMessage('globalSuccessMsg', { clearOnXTimeNavigate: 1 });
			this.goBack();
		});
	}

	goBack() {
		this.router.navigate(['/admin-panel/multi-tariff/profiles']);
	}
}
